import type { Priority } from "@app/core";
import {
  applyOverrides,
  buildRecurrenceRule,
  createOverride,
  createTask,
  effectiveBoundary,
  newId,
  parseRRule,
  resolveEffectiveRrule,
} from "@app/core";
import { getServices } from "../../platform.js";
import { buildEffectiveTaskFields, partitionAndDelete } from "./helpers.js";
import { remapSingleRef } from "./remap-single-ref.js";
import type { SeriesGet, SeriesSet } from "./types.js";

export interface SplitPatch {
  /** New dtstart for the new series. Defaults to occurrenceIso (same slot). */
  newDueAt?: string | undefined;
  /** Reshaped rrule string. undefined = resolveEffectiveRrule at split point.
   *  null = use base rule.rrule. A string is used verbatim. (Tri-state.) */
  newRrule?: string | null | undefined;
  /** Field overrides for the new task. undefined = inherit effective value at split. */
  title?: string | undefined;
  notes?: string | undefined;
  duration_minutes?: number | undefined;
  priority?: Priority | undefined;
  /** Real nullable field — null clears the custom colour. undefined = inherit. */
  custom_color?: string | null | undefined;
  all_day?: boolean | undefined;
  /** Topics for the tail segment. undefined = inherit the source's topics. */
  topic_ids?: string[] | undefined;
  /** Labels for the tail segment. undefined = inherit the source's labels. */
  label_ids?: string[] | undefined;
  /** New time zone for the tail segment. undefined = inherit the source rule's tz. */
  tz?: string | null | undefined;
}

/**
 * Core series-split primitive. Caps the original series at `occurrenceIso`,
 * creates an independent rule + task for the segment from `patch.newDueAt`
 * onwards, migrates overrides, and updates the Zustand store atomically.
 *
 * All "this and following" operations (drag-to-move, drag-to-resize, edit
 * dialog property changes) delegate here so the split logic lives in exactly
 * one place.
 */
export async function splitSeriesAt(
  set: SeriesSet,
  get: SeriesGet,
  sourceId: string,
  occurrenceIso: string,
  patch: SplitPatch,
): Promise<void> {
  const source = get().tasks.find((t) => t.id === sourceId);
  if (!source || !source.recurrence_id) return;

  const { recurrence, recurrenceOverrides, tasks: tasksRepo } = getServices();
  const rule = get().rules[source.recurrence_id]
    ?? await recurrence.findById(source.recurrence_id);
  if (!rule) return;

  const overrides = get().overrides[rule.id] ?? [];
  const eff = applyOverrides(source, occurrenceIso, overrides);

  // rrule in effect at the split point in the OLD series — becomes the new
  // series' default cadence and anchors the single-override ordinal remap below.
  const effRruleAtSplit = resolveEffectiveRrule(rule, occurrenceIso, overrides);
  // rrule for the NEW series (caller may supply a reshaped one, e.g. day-of-week change).
  const effRrule = patch.newRrule !== undefined
    ? (patch.newRrule ?? rule.rrule)
    : effRruleAtSplit;

  // Remove superseded thisandfuture overrides on the old rule.
  const { removed: toDelete, remaining: afterDelete } = await partitionAndDelete(
    overrides,
    (o) => o.range === "thisandfuture" && effectiveBoundary(o) >= occurrenceIso,
  );
  let remaining = afterDelete;

  // Move single overrides at-or-after the split to the new rule. We remap each
  // override's recurrence_id_ref to the corresponding occurrence in the new rule
  // (matching by ordinal in the segment), otherwise a cancel-override or
  // modify-override keyed to the OLD occurrence ISO is stranded after the split
  // and silently stops applying.
  const toMigrate = remaining.filter(
    (o) => o.range === "single" && o.recurrence_id_ref >= occurrenceIso,
  );
  remaining = remaining.filter((o) => !toMigrate.some((m) => m.id === o.id));

  const now = new Date().toISOString();
  const newDueAt = patch.newDueAt ?? occurrenceIso;

  // Pure time-shift semantics for "this and following": the dragged occurrence
  // and every following occurrence move by EXACTLY `delta`, the inter-occurrence
  // spacing is preserved (the new rule keeps the same cadence), and the series
  // end date shifts by EXACTLY the same delta. Nothing is ever deleted or newly
  // generated — `until` is just the old exclusive boundary translated by `delta`,
  // so the shifted segment has the same number of occurrences as before.
  const delta = new Date(newDueAt).getTime() - new Date(occurrenceIso).getTime();

  // Build new rule.
  const newRuleBase = buildRecurrenceRule(parseRRule(effRrule), new Date(newDueAt));
  const fields = buildEffectiveTaskFields(
    {
      title: patch.title,
      notes: patch.notes,
      all_day: patch.all_day,
      duration_minutes: patch.duration_minutes,
      priority: patch.priority,
      custom_color: patch.custom_color,
      topic_ids: patch.topic_ids,
      label_ids: patch.label_ids,
      tz: patch.tz,
    },
    eff,
    source,
  );

  const newUntil = rule.until
    ? new Date(new Date(rule.until).getTime() + delta).toISOString()
    : null;

  const newRule = { ...newRuleBase, until: newUntil, tz: patch.tz !== undefined ? patch.tz : rule.tz };
  const newTask = createTask({
    ...fields,
    due_at: newDueAt,
    recurrence_id: newRule.id,
  });

  // Cap the original series at the split point.
  const cappedRule = { ...rule, until: occurrenceIso, updated_at: now };
  const capOverride = createOverride({
    recurrence_id: rule.id,
    recurrence_id_ref: occurrenceIso,
    range: "thisandfuture",
    status: "cancelled",
  });

  // Persist.
  await recurrence.update(cappedRule);
  await recurrence.insert(newRule);
  await tasksRepo.insert(newTask);
  const newRuleOverrides: typeof overrides = [];

  // Replicated thisandfuture overrides belong to the shifted tail, so their
  // time anchors move by the same delta. Critically, this carries a *prior
  // split's* cap override (thisandfuture/cancelled at the old cap point) to the
  // new cap point instead of leaving it on top of the shifted last occurrence —
  // otherwise re-splitting an already-split series would cancel that occurrence.
  for (const o of toDelete) {
    const shiftedRef = new Date(new Date(o.recurrence_id_ref).getTime() + delta).toISOString();
    const shiftedDueAt = o.due_at ? new Date(new Date(o.due_at).getTime() + delta).toISOString() : o.due_at;
    const rep = {
      ...o,
      id: newId(),
      recurrence_id: newRule.id,
      recurrence_id_ref: shiftedRef,
      due_at: shiftedDueAt,
      created_at: now,
      updated_at: now,
    };
    await recurrenceOverrides.insert(rep);
    newRuleOverrides.push(rep);
  }
  for (const o of toMigrate) {
    await recurrenceOverrides.hardDelete(o.id);
    const remappedRef = remapSingleRef(
      { ...rule, rrule: effRruleAtSplit },
      newRule,
      newDueAt,
      occurrenceIso,
      o.recurrence_id_ref,
    );
    if (remappedRef === null) continue; // stranded — drop instead of orphaning in DB
    const mig = {
      ...o,
      id: newId(),
      recurrence_id: newRule.id,
      recurrence_id_ref: remappedRef,
      created_at: now,
      updated_at: now,
    };
    await recurrenceOverrides.insert(mig);
    newRuleOverrides.push(mig);
  }
  await recurrenceOverrides.insert(capOverride);

  const oldRuleOverrides = [...remaining, capOverride].sort(
    (a, b) => a.recurrence_id_ref.localeCompare(b.recurrence_id_ref),
  );
  newRuleOverrides.sort((a, b) => a.recurrence_id_ref.localeCompare(b.recurrence_id_ref));

  set({
    tasks: [...get().tasks, newTask],
    rules: { ...get().rules, [rule.id]: cappedRule, [newRule.id]: newRule },
    overrides: {
      ...get().overrides,
      [rule.id]: oldRuleOverrides,
      [newRule.id]: newRuleOverrides,
    },
  });
}
